import range from 'lodash-es/range';
import zip from 'lodash-es/zip';
import React from 'react';
import {
  GraphExportType,
} from '../../Utils';
import {
  LayoutText,
} from '../../workerStore/rings/Utils';
import {
  TextType,
} from '../../workerStore/rings/Utils';
import {
  decideColorForNode,
} from '../chart/Utils';
import {
  centerNodeTextColor,
} from '../Utils';

interface IProps {
  texts: LayoutText[];
  exportType: GraphExportType;
}

type ILayoutInput = {
  centerX: number,
  centerY: number,
  textSplitIntoLines: string[];
  fontSize: number;
  color: string;
} & (
  {type: TextType.InCircle} |
  {type: TextType.InRectangle; angle: number; }
);

const getTextInRectangleGetter =
  (exportType: GraphExportType) =>

    (text: ILayoutInput, isActive: boolean, isCurrentlyExported: boolean, id: number): JSX.Element | null => {

    const {
      centerX, centerY, textSplitIntoLines, fontSize, color,
    } = text;

    let transform: string;
    if (text.type === TextType.InRectangle) {
      const {angle} = text;
      // Need to adjust the angle so that texts don't appear upside down
      // while rotated: This happens when the rotation is between 90deg and
      // 270deg. The adjustment doesn't change the label's position and
      // apparent rotation but changes the rotation of text:
      const adjustedAngle = (angle < 90 || angle > 270) ? angle : angle + 180;
      transform = `rotate(${adjustedAngle} ${centerX}, ${centerY})`;

    } else {
      transform = '';
    }

    const textColor = decideColorForNode(
      id, undefined, color, isActive, isCurrentlyExported,
    );
    const numOfLines =  textSplitIntoLines.length;

    const lineHeight = fontSize;

    // This is the top of the rectangle:
    const yCoordinateOfRectTop = centerY - (numOfLines / 2) * lineHeight;

    let baselineOfFirstLineOfText: number, alignmentBaseline: 'middle' | 'auto';
    if (exportType === GraphExportType.SVG) {
      // Align text's middle baseline with line's middle because SVG
      // supports `alignment-baseline=middle` (whereas the PNG/PDF converter
      // doesn't):
      baselineOfFirstLineOfText = yCoordinateOfRectTop + lineHeight * 0.5;
      alignmentBaseline = 'middle';
    } else {
      // Multiply `lineHeight` by 0.75 because we assume that the actual
      // height of the characters is only about half the height of a line.
      // This implies that the baseline of a line of text is 3/4 from the
      // top of that line:
      baselineOfFirstLineOfText = yCoordinateOfRectTop + lineHeight * 0.75;
      alignmentBaseline = 'auto';
    }

    const textBaselines = range(0, numOfLines).map(index => baselineOfFirstLineOfText + index * lineHeight);

    const zipped = zip(textSplitIntoLines, textBaselines as any) as Array<[string, number]>;

    const lines = zipped.map(([textInLine, baseline], index) => (
      <tspan x={centerX} y={baseline} textAnchor='middle'
        alignmentBaseline={alignmentBaseline} key={index} fontSize={fontSize}>
        {textInLine}
      </tspan>
    ));

    return (
      <text transform={transform} textAnchor='middle' fill={textColor} key={id}>
        {lines}
      </text>
    );

};

export default class extends React.Component<IProps> {
  render() {
    const {texts, exportType} = this.props;

    const getTextInRectangle = getTextInRectangleGetter(exportType);
    const textElems = texts.map(datum => {
      if (datum.type === TextType.InRectangle) {
        if (datum.text.showText === true) {
          const {
            centerX, centerY, angle, textSplitIntoLines, fontSize, color,
          } = datum.text;
          const layoutInput: ILayoutInput = {
            centerX, centerY, angle, textSplitIntoLines, fontSize, color,
            type: TextType.InRectangle,
          };
          return getTextInRectangle(
            layoutInput, datum.isActive, datum.isCurrentlyExported, datum.id,
          );

        } else {
          return null;
        }
      } else {
        if (datum.text.showText === true) {
          const {
            centerX, centerY, textSplitIntoLines, fontSize,
          } = datum.text;
          const layoutInput: ILayoutInput = {
            centerX, centerY, textSplitIntoLines, fontSize,
            type: TextType.InCircle,
            color: centerNodeTextColor,
          };
          return getTextInRectangle(
            layoutInput, datum.isActive, datum.isCurrentlyExported, datum.id,
          );
        } else {
          return null;
        }
      }
    });
    return textElems as any;
  }
}
